import React, { useEffect, useState } from "react";
import styles from "./CardSelectorDialog.module.scss";
import { ChervonLeft } from "../../../../assets/icons";
import CardOption from "./CardOption";
import { useAuthorizeCardTransactionMutation, useLazyGetCardsQuery, useVerifyPendingCardsMutation } from "../../../../service/paystack-service";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import { AUTHORIZATION_STATUS } from "../../../../pages/customer/dashboard/constant";
import { CustomerPaystackCard } from "../../../../model/customer/customer";
import { ApplicationRoutes } from "../../../../routes";

type Props = {
	selectedCard?: CustomerPaystackCard;
	onSelectCard: (card?: CustomerPaystackCard) => void;
	onCancel: () => void;
};

const CardSelectorDialog: React.FC<Props> = (props: Props) => {
	const [getCards, { isLoading: loadingCards, data }] = useLazyGetCardsQuery();
	const [verifyPendingCards, { isLoading: verifyingCards }] = useVerifyPendingCardsMutation();
	const [initiateAddCard, { isLoading: initiatingAddCard }] = useAuthorizeCardTransactionMutation();
	const [selectedCard, setSelectedCard] = useState(props.selectedCard);

	const verifyPending = () => {
		verifyPendingCards()
			.unwrap()
			.catch(error => {
				toast.error(error.data?.title || "Something went wrong");
			});
	};

	const addCard = () => {
		initiateAddCard({	
			redirectUrl: `${window.location.origin}/${ApplicationRoutes.BILLING_INFO}?status=${AUTHORIZATION_STATUS.CARD_SUCESS}`,
			failedRedirectUrl: `${window.location.origin}/${ApplicationRoutes.BILLING_INFO}?failed=true`,
		})
			.unwrap()
			.then(res => {
				window.location.href = res.authorizationUrl;
			})
			.catch(error => {
				toast.error(error.data?.title || "Something went wrong");
			});
	};

	const handleCardSelection = (card: CustomerPaystackCard) => {
		setSelectedCard(card);
	};

	const onSave = () => {
		props.onSelectCard(selectedCard);
	};

	useEffect(() => {
		getCards(undefined, true)
			.unwrap()
			.catch(error => {
				toast.error(error.data?.title || "Something went wrong");
			});
	}, []);

	useEffect(() => {
		setSelectedCard(props.selectedCard);
	}, [props.selectedCard]);

	return (
		<div className={styles.container}>
			<div className={styles.titleContainer}>
				<div className={styles.title}>Card Payment</div>
				<div className={styles.subtitle}>Manage your Card Payment</div>
			</div>
			<div className={styles.cardTitle}>My Cards</div>
			<div className={styles.cardsContainer}>
				{(() => {
					if (loadingCards) {
						return <BounceLoader color="#DE841B" size={25} />;
					}

					if (data?.length === 0) {
						return (
							<div className={styles.noCardContainer}>
								<div className={styles.noCardTitle}>You have no saved card</div>
								<div className={styles.noCardSubtitle}>Add a card to make payment</div>
							</div>
						);
					}

					return data?.map(card => <CardOption key={card.id} selected={selectedCard?.id === card.id} onSelect={handleCardSelection} card={card} />);
				})()}
			</div>
			{data?.findIndex(card => card.status !== "Success") !== -1 && (
				<button className={`standardButton ${styles.verifyCardsButton}`} onClick={verifyPending} disabled={verifyingCards}>
					{verifyingCards ? <BounceLoader color="#DE841B" size={25} /> : "Verify Pending Cards"}
				</button>
			)}
			<button className={`standardButton ${styles.addCardButton}`} onClick={addCard} disabled={initiatingAddCard}>
				{initiatingAddCard ? <BounceLoader color="#DE841B" size={25} /> : "Add Card"}
			</button>

			<div className={styles.buttonContainer}>
				<button className={`standardButton ${styles.cancelButton}`} onClick={props.onCancel}>
					<ChervonLeft /> Cancel
				</button>
				<button className={`standardButton ${styles.saveButton}`} onClick={onSave}>
					Save <ChervonLeft />
				</button>
			</div>
		</div>
	);
};

export default CardSelectorDialog;
